// src/ordenes/CrearOrden.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
} from "@mui/material";

export const CrearOrden = () => {
  const toast = useToast();

  const [clientes, setClientes] = useState([]);
  const [centros, setCentros] = useState([]);

  const [clienteId, setClienteId] = useState(""); // string/number id
  const [centrosIds, setCentrosIds] = useState([]); // array ids

  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");

  const [loadingClientes, setLoadingClientes] = useState(true);
  const [loadingCentros, setLoadingCentros] = useState(false);
  const [saving, setSaving] = useState(false);

  // =========================
  // Cargar clientes
  // =========================
  useEffect(() => {
    fetchClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchClientes = async () => {
    setLoadingClientes(true);
    try {
      const res = await fetch(`${Global.url}clientes`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!res.ok) {
        toast.error("No se pudieron cargar los clientes.");
        setClientes([]);
        return;
      }

      const data = await res.json().catch(() => []);
      setClientes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error al cargar clientes:", err);
      toast.error("Error de conexión al cargar clientes.");
      setClientes([]);
    } finally {
      setLoadingClientes(false);
    }
  };

  // =========================
  // Cargar centros por cliente
  // =========================
  useEffect(() => {
    if (!clienteId) {
      setCentros([]);
      setCentrosIds([]);
      return;
    }
    fetchCentros(clienteId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const fetchCentros = async (cliId) => {
    setLoadingCentros(true);
    setCentros([]);
    setCentrosIds([]);
    try {
      const res = await fetch(`${Global.url}centros/cliente/${cliId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!res.ok) {
        toast.error("No se pudieron cargar los centros del cliente.");
        return;
      }

      const data = await res.json().catch(() => []);
      setCentros(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error al cargar centros:", err);
      toast.error("Error de conexión al cargar centros.");
    } finally {
      setLoadingCentros(false);
    }
  };

  // =========================
  // Autocomplete options
  // =========================
  const clientesOrdenados = useMemo(() => {
    return (clientes || []).slice().sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [clientes]);

  const clienteObj =
    clientesOrdenados.find((c) => String(c.id) === String(clienteId)) || null;

  const centrosOrdenados = useMemo(() => {
    return (centros || []).slice().sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [centros]);

  const centrosSeleccionadosObj = useMemo(() => {
    const set = new Set((centrosIds || []).map(String));
    return centrosOrdenados.filter((c) => set.has(String(c.id)));
  }, [centrosOrdenados, centrosIds]);

  // =========================
  // Submit
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!titulo.trim() || !descripcion.trim() || !clienteId || !centrosIds.length) {
      toast.warning("Cliente, título, descripción y al menos un centro son obligatorios.");
      return;
    }

    setSaving(true);
    try {
      // 1) Crear orden
      const resOrden = await fetch(`${Global.url}ordenes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          cliente_id: clienteId,
          titulo: titulo.trim(),
          descripcion: descripcion.trim(),
        }),
      });

      const dataOrden = await resOrden.json().catch(() => ({}));

      if (!resOrden.ok) {
        toast.error(dataOrden?.message || "Error al crear la orden.");
        return;
      }

      const ordenId = dataOrden?.data?.id ?? dataOrden?.id;
      if (!ordenId) {
        toast.error("La orden se creó, pero no se pudo obtener su ID.");
        return;
      }

      // 2) Asociar centros
      const resAsociacion = await fetch(`${Global.url}ordenes/asociar-centros`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          orden_id: ordenId,
          centros_id: centrosIds,
        }),
      });

      if (!resAsociacion.ok) {
        toast.warning("La orden fue creada, pero no se pudieron asociar los centros.");
        return;
      }

      toast.success("Orden creada y centros asociados correctamente.");

      // Reset
      setTitulo("");
      setDescripcion("");
      setClienteId("");
      setCentros([]);
      setCentrosIds([]);
    } catch (error) {
      console.error("Error al crear orden:", error);
      toast.error("Error inesperado al crear la orden.");
    } finally {
      setSaving(false);
    }
  };

  const limpiarFormulario = () => {
    if (saving) return;
    setTitulo("");
    setDescripcion("");
    setClienteId("");
    setCentros([]);
    setCentrosIds([]);
    toast.info("Formulario limpiado.");
  };

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer">
      <Stack spacing={2}>
        <Box className="pageHeader">
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Crear Nueva Orden de Trabajo
          </Typography>
        </Box>

        <Box className="contentContainer contentContainer--formSingle">
          <Paper sx={{ p: 2 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                {/* Título */}
                <TextField
                  label="Título"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  placeholder="Título de la orden"
                  required
                />

                {/* Cliente */}
                <Autocomplete
                  size="small"
                  options={clientesOrdenados}
                  getOptionLabel={(c) => c?.nombre || ""}
                  isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                  value={clienteObj}
                  onChange={(_, newValue) => setClienteId(newValue ? String(newValue.id) : "")}
                  loading={loadingClientes}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      placeholder={loadingClientes ? "Cargando clientes..." : "Selecciona un cliente"}
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingClientes ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  clearOnEscape
                  noOptionsText={loadingClientes ? "Cargando..." : "No hay clientes"}
                />

                {/* Centros */}
                <Autocomplete
                  multiple
                  size="small"
                  options={centrosOrdenados}
                  getOptionLabel={(c) => c?.nombre || ""}
                  isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                  value={centrosSeleccionadosObj}
                  onChange={(_, newValues) => setCentrosIds(newValues.map((c) => c.id))}
                  loading={loadingCentros}
                  disabled={!clienteId || loadingCentros}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Centros asociados"
                      placeholder={
                        !clienteId
                          ? "Selecciona primero un cliente"
                          : loadingCentros
                            ? "Cargando centros..."
                            : "Selecciona uno o varios centros"
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCentros ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  noOptionsText={
                    !clienteId
                      ? "Selecciona antes un cliente"
                      : loadingCentros
                        ? "Cargando..."
                        : "No hay centros para este cliente"
                  }
                  clearOnEscape
                />

                {/* Descripción */}
                <TextField
                  label="Descripción"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="Describe brevemente la orden de trabajo"
                  multiline
                  minRows={4}
                  required
                />

                {/* Acciones */}
                <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
                  <Button variant="outlined" onClick={limpiarFormulario} disabled={saving}>
                    Limpiar
                  </Button>

                  <Button type="submit" disabled={saving} aria-busy={saving} sx={{ height: 40 }}>
                    {saving ? "Creando..." : "Crear Orden"}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};
